import { useEffect, useState } from 'react';

// A tab that stays open never picks up a new deploy on its own: the whole app
// is already loaded in memory, so a tablet left open all day keeps running the
// old code (that's how a fixed bug kept happening on the warehouse tablet).
// Every build's main bundle has a content hash in its filename, so comparing
// the one this tab loaded with the one the live index.html points to tells us
// when a newer version is out. We only ever offer the reload — reloading on our
// own could throw away scanning progress that hasn't been saved yet.
const CHECK_EVERY_MS = 60 * 1000;
const SNOOZE_MS = 10 * 60 * 1000;

const bundleName = (text) => text?.match(/main\.[a-f0-9]+\.js/)?.[0] ?? null;

export default function UpdateBanner() {
  const [updateReady, setUpdateReady] = useState(false);
  const [snoozed, setSnoozed] = useState(false);

  useEffect(() => {
    const script = document.querySelector('script[src*="/static/js/main."]');
    const running = bundleName(script?.getAttribute('src'));
    if (!running) return; // dev server serves bundle.js — nothing to compare

    let found = false;
    const check = async () => {
      if (found || document.hidden) return;
      try {
        const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const live = bundleName(await res.text());
        if (live && live !== running) {
          found = true;
          setUpdateReady(true);
        }
      } catch { /* offline or a blip — the next check will try again */ }
    };

    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    // Tablets sleep between orders; check as soon as the screen comes back.
    const onWake = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  useEffect(() => {
    if (!snoozed) return;
    const t = setTimeout(() => setSnoozed(false), SNOOZE_MS);
    return () => clearTimeout(t);
  }, [snoozed]);

  if (!updateReady || snoozed) return null;

  return (
    <div role="status" aria-live="polite" style={s.bar}>
      <div style={s.text}>
        <div style={s.title}>A new version of 2Check is available</div>
        <div style={s.sub}>Save any order in progress, then tap Reload.</div>
      </div>
      <div style={s.actions}>
        <button type="button" style={s.later} onClick={() => setSnoozed(true)}>Later</button>
        <button type="button" style={s.reload} onClick={() => window.location.reload()}>Reload</button>
      </div>
    </div>
  );
}

const s = {
  // Above modals (1000) so it's never hidden, below the loading overlay (2000).
  bar: {
    position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)',
    width: 'min(520px, calc(100% - 32px))', zIndex: 1500,
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    background: 'var(--surface)', border: '1px solid var(--cyan-mid)', borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)', padding: '12px 14px', fontFamily: 'var(--font-ui)',
  },
  text: { flex: '1 1 220px', minWidth: 0 },
  title: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  sub: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 },
  actions: { display: 'flex', gap: 8, marginLeft: 'auto' },
  later: {
    padding: '8px 14px', background: 'none', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  reload: {
    padding: '8px 16px', background: 'var(--cyan)', border: 'none', borderRadius: 'var(--radius-sm)',
    color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
};
